import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Bell,
  FileText,
  Shirt,
  Calendar,
  Check,
  Loader2,
  ArrowRight,
  Clock,
  MapPin,
  Trophy,
  Shield,
  UserCheck,
  UserX,
} from 'lucide-react';
import api from '../../services/api';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [dismissing, setDismissing] = useState(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      // Fetch ALL notifications including dismissed ones
      const response = await api.get('/notifications?include_dismissed=true');
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async (type, referenceId, label) => {
    setDismissing(`${type}_${referenceId}`);
    try {
      await api.post(`/notifications/${type}/${referenceId}/dismiss`);
      toast.success(`Marked as ${label}`);
      // Update status in list instead of removing
      setNotifications(prev => prev.map(n => 
        (n.type === type && n.reference_id === referenceId) 
          ? { ...n, status: 'dismissed' } 
          : n
      ));
    } catch (error) {
      toast.error('Failed to dismiss notification');
    } finally {
      setDismissing(null);
    }
  };

  const handleAction = (notification) => {
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'document':
        return <FileText className="w-5 h-5" />;
      case 'uniform_campaign':
        return <Shirt className="w-5 h-5" />;
      case 'event':
        return <Calendar className="w-5 h-5" />;
      case 'game_rsvp':
        return <Trophy className="w-5 h-5" />;
      case 'player_selection':
        return <UserCheck className="w-5 h-5" />;
      case 'player_removal':
        return <UserX className="w-5 h-5" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'document':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'uniform_campaign':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'event':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'game_rsvp':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'player_selection':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'player_removal':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'document':
        return 'Document';
      case 'uniform_campaign':
        return 'Uniform';
      case 'event':
        return 'Event';
      case 'game_rsvp':
        return 'Game';
      case 'player_selection':
        return 'Selection';
      case 'player_removal':
        return 'Removed';
      default:
        return 'Notification';
    }
  };

  const getDismissLabel = (type) => {
    switch (type) {
      case 'document':
        return 'Reviewed';
      case 'uniform_campaign':
        return 'Submitted';
      case 'event':
        return 'Dismiss';
      case 'game_rsvp':
        return 'Dismiss';
      case 'player_selection':
        return 'Dismiss';
      case 'player_removal':
        return 'Dismiss';
      default:
        return 'Dismiss';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  // Filter notifications by tab
  const filteredNotifications = activeTab === 'all' 
    ? notifications 
    : activeTab === 'player_selection'
      ? notifications.filter(n => n.type === 'player_selection' || n.type === 'player_removal')
      : notifications.filter(n => n.type === activeTab);

  const counts = {
    all: notifications.length,
    document: notifications.filter(n => n.type === 'document').length,
    uniform_campaign: notifications.filter(n => n.type === 'uniform_campaign').length,
    event: notifications.filter(n => n.type === 'event').length,
    game_rsvp: notifications.filter(n => n.type === 'game_rsvp').length,
    player_selection: notifications.filter(n => n.type === 'player_selection' || n.type === 'player_removal').length,
  };

  // Count active (non-dismissed) notifications
  const activeCount = notifications.filter(n => n.status !== 'dismissed').length;

  if (loading) {
    return (
      <DashboardLayout title="Notifications">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Notifications">
      <div className="space-y-6" data-testid="notifications-page">
        {/* Header */}
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Notifications</h1>
          <p className="text-slate-600">
            Stay updated with documents, uniforms, and events
            {activeCount > 0 && <span className="ml-2 text-blue-600">({activeCount} active)</span>}
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex w-full max-w-2xl overflow-x-auto">
            <TabsTrigger value="all" className="gap-1 text-xs lg:text-sm whitespace-nowrap">
              <Bell className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
              All ({counts.all})
            </TabsTrigger>
            <TabsTrigger value="player_selection" className="gap-1 text-xs lg:text-sm whitespace-nowrap" data-testid="tab-selections">
              <UserCheck className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
              ({counts.player_selection})
            </TabsTrigger>
            <TabsTrigger value="game_rsvp" className="gap-1 text-xs lg:text-sm whitespace-nowrap">
              <Trophy className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
              ({counts.game_rsvp})
            </TabsTrigger>
            <TabsTrigger value="document" className="gap-1 text-xs lg:text-sm whitespace-nowrap">
              <FileText className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
              ({counts.document})
            </TabsTrigger>
            <TabsTrigger value="uniform_campaign" className="gap-1 text-xs lg:text-sm whitespace-nowrap">
              <Shirt className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
              ({counts.uniform_campaign})
            </TabsTrigger>
            <TabsTrigger value="event" className="gap-1 text-xs lg:text-sm whitespace-nowrap">
              <Calendar className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
              ({counts.event})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {filteredNotifications.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <Bell className="w-16 h-16 mb-4 text-slate-300" />
                  <p className="text-lg font-medium">No notifications</p>
                  <p className="text-sm mt-1">You're all caught up!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredNotifications.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                    onDismiss={handleDismiss}
                    onAction={handleAction}
                    dismissing={dismissing}
                    getIcon={getIcon}
                    getTypeColor={getTypeColor}
                    getTypeLabel={getTypeLabel}
                    getDismissLabel={getDismissLabel}
                    formatTime={formatTime}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

// Notification Card Component
const NotificationCard = ({ 
  notification, 
  onDismiss, 
  onAction, 
  dismissing,
  getIcon,
  getTypeColor,
  getTypeLabel,
  getDismissLabel,
  formatTime,
  formatDate
}) => {
  const isDismissing = dismissing === `${notification.type}_${notification.reference_id}`;
  const isRead = notification.status === 'read';

  return (
    <Card 
      className={`transition-all ${isRead ? 'opacity-70' : 'border-l-4 border-l-blue-500'}`}
      data-testid={`notification-${notification.id}`}
    >
      <CardContent className="p-3 lg:p-4">
        <div className="flex items-start gap-2.5 lg:gap-4">
          {/* Icon */}
          <div className={`p-1.5 lg:p-2 rounded-lg shrink-0 ${getTypeColor(notification.type)}`}>
            {getIcon(notification.type)}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 lg:gap-2 mb-0.5 lg:mb-1 flex-wrap">
              <Badge variant="outline" className="text-[10px] lg:text-xs px-1.5 py-0">
                {getTypeLabel(notification.type)}
              </Badge>
              <span className="text-[10px] lg:text-xs text-slate-400">
                {formatTime(notification.created_at)}
              </span>
              {notification.status === 'unread' && (
                <span className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full bg-blue-500" />
              )}
            </div>

            <h3 className="font-semibold text-sm lg:text-base text-slate-900 leading-tight">{notification.title}</h3>
            
            {notification.message && (
              <p className="text-xs lg:text-sm text-slate-600 mt-0.5 lg:mt-1 leading-snug">{notification.message}</p>
            )}

            {/* Extra info based on type */}
            {notification.type === 'event' && notification.data && (
              <div className="flex items-center gap-3 mt-1.5 text-[10px] lg:text-xs text-slate-500">
                {notification.data.start_datetime && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3 shrink-0" />
                    {formatDate(notification.data.start_datetime)}
                  </span>
                )}
                {notification.data.location && (
                  <span className="flex items-center gap-1 truncate">
                    <MapPin className="w-3 h-3 shrink-0" />
                    {notification.data.location}
                  </span>
                )}
              </div>
            )}

            {notification.type === 'game_rsvp' && notification.data && (
              <div className="flex items-center gap-3 mt-1.5 text-[10px] lg:text-xs text-slate-500">
                {notification.data.game_date && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3 shrink-0" />
                    {formatDate(notification.data.game_date)}
                  </span>
                )}
                {notification.data.team_name && (
                  <span className="flex items-center gap-1">
                    <Shield className="w-3 h-3 shrink-0" />
                    {notification.data.team_name}
                  </span>
                )}
                {notification.data.location && (
                  <span className="flex items-center gap-1 truncate">
                    <MapPin className="w-3 h-3 shrink-0" />
                    {notification.data.location}
                  </span>
                )}
              </div>
            )}

            {(notification.type === 'player_selection' || notification.type === 'player_removal') && notification.data && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-[10px] lg:text-xs text-slate-500">
                {notification.data.tournament_name && (
                  <span className="flex items-center gap-1">
                    <Trophy className="w-3 h-3 shrink-0" />
                    {notification.data.tournament_name}
                  </span>
                )}
                {notification.data.game_date && (
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <Clock className="w-3 h-3 shrink-0" />
                    {notification.data.game_date}{notification.data.start_time ? ` ${notification.data.start_time}` : ''}
                  </span>
                )}
                {notification.data.location && (
                  <span className="flex items-center gap-1 truncate">
                    <MapPin className="w-3 h-3 shrink-0" />
                    {notification.data.location}
                  </span>
                )}
              </div>
            )}

            {notification.type === 'uniform_campaign' && notification.deadline && (
              <p className="text-[10px] lg:text-xs text-orange-600 mt-1.5">
                <Clock className="w-3 h-3 inline mr-1" />
                Deadline: {formatDate(notification.deadline)}
              </p>
            )}

            {/* Actions - inline on mobile */}
            <div className="flex items-center gap-2 mt-2">
              <Button
                size="sm"
                className="h-7 text-xs lg:h-9 lg:text-sm"
                onClick={() => onAction(notification)}
                data-testid={`action-${notification.id}`}
              >
                {notification.action_label || 'View'}
                <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
              
              {notification.status !== 'read' && notification.status !== 'dismissed' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs lg:h-9 lg:text-sm"
                  onClick={() => onDismiss(notification.type, notification.reference_id, getDismissLabel(notification.type))}
                  disabled={isDismissing}
                  data-testid={`dismiss-${notification.id}`}
                >
                  {isDismissing ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-3 h-3 mr-1" />
                      {getDismissLabel(notification.type)}
                    </>
                  )}
                </Button>
              )}
              
              {notification.status === 'dismissed' && (
                <Badge variant="outline" className="text-[10px] lg:text-xs text-green-600 border-green-300">
                  <Check className="w-3 h-3 mr-1" />
                  {getDismissLabel(notification.type)}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationsPage;
